import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../template/store';
import axiosInstance from '../../../axiosSetup';
import { unsubscribeNotification } from '../../../layouts/NotificationModal';

type FormValues = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const ChangePasswordForm = () => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const APP_PATH_PREFIX = process.env.REACT_APP_APP_PATH_PREFIX || '';

  const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
  const email: string = profileDetails?.email ?? '';

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string().required(
      t('resource_3:isRequired', { field: 'Old Password' })
    ),
    newPassword: Yup.string()
      .required(t('resource_3:isRequired', { field: 'New Password' }))
      .min(8, t('resource_message:passwordLength'))
      .matches(/^(?=.*[a-z])/, t('resource_message:passwordShouldContainUpparAndLowerCase'))
      .matches(/^(?=.*[A-Z])/, t('resource_message:passwordShouldContainUpparAndLowerCase'))
      .matches(/^(?=.*[0-9])/, t('resource_message:passwordShouldContainNo')),
    confirmPassword: Yup.string()
      .required(t('resource_3:isRequired', { field: 'Confirm Password' }))
      .oneOf([Yup.ref('newPassword')], t('resource_message:passwordsMustMatch')),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const handleLogoutAndRedirect = async () => {
    try {
      const isTpdcs = window.location.href.includes('tpdcs');
      const primaryApp = isTpdcs ? '.tpdcs.com' : '.odexglobal.com';
      const windowLoc = window.location.href.indexOf(primaryApp);
      const windowurl = window.location.href.substring(0, windowLoc);
      const dropUrl = isTpdcs
        ? `${windowurl}${primaryApp}/drop`
        : `${windowurl}${primaryApp}${APP_PATH_PREFIX}/drop`;

      await unsubscribeNotification();
      await axiosInstance.get('/api/user-management/v1/users/logout');
      window.location.href = dropUrl;
    } catch (error) {
      console.error('Logout after password change failed:', error);
      // Fall back to /app so the session refresh happens there
      window.location.href = `${APP_PATH_PREFIX}/app`;
    }
  };

  const onSubmit = handleSubmit(
    async (payload) => {
      setSubmitting(true);
      try {
        const response = await axiosInstance.post(
          '/api/user-management/v1/users/changePassword',
          {
            emailId: email,
            oldPassword: payload.oldPassword,
            newPassword: payload.newPassword,
            confirmPassword: payload.confirmPassword,
            isTNCAccepted: 'Y',
          }
        );

        const { responseCode, responseMsg } = response.data;
        if (responseCode === 'SUCCESS') {
          toast.success(responseMsg ?? 'Password changed successfully.');
          reset();
          await handleLogoutAndRedirect();
        } else {
          toast.error(responseMsg ?? 'Failed to change password.');
        }
      } catch (error: any) {
        const message = error?.response?.data?.responseMsg;
        toast.error(message ?? 'Failed to change password. Please try again.');
        console.error('Change password error:', error);
      } finally {
        setSubmitting(false);
      }
    },
    (error: any) => {
      console.error('Handle submit error: ', error);
    }
  );

  return (
    <Form onSubmit={onSubmit}>
      <Form.Group className='mb-3 mt-2'>
        <Form.Label className='fs--1'>{t('resource_2:emailAddress')}</Form.Label>
        <Form.Control
          type='email'
          value={email}
          disabled
          className='fs--1 inputBorder form-control'
        />
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label className='fs--1'>{t('resource_2:oldPassword')}</Form.Label>
        <Form.Control
          type='password'
          placeholder={t('resource_2:oldPassword')}
          {...register('oldPassword')}
          className={`fs--1 inputBorder form-control ${
            errors.oldPassword ? 'is-invalid' : ''
          }`}
        />
        <div className='invalid-feedback'>
          {errors.oldPassword?.message?.toString()}
        </div>
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label className='fs--1'>{t('resource_2:newPassword')}</Form.Label>
        <Form.Control
          type='password'
          placeholder={t('resource_2:newPassword')}
          {...register('newPassword')}
          className={`fs--1 inputBorder form-control ${
            errors.newPassword ? 'is-invalid' : ''
          }`}
        />
        <div className='invalid-feedback'>
          {errors.newPassword?.message?.toString()}
        </div>
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label className='fs--1'>{t('resource_2:confirmPassword')}</Form.Label>
        <Form.Control
          type='password'
          placeholder={t('resource_2:confirmPassword')}
          {...register('confirmPassword')}
          className={`fs--1 inputBorder form-control ${
            errors.confirmPassword ? 'is-invalid' : ''
          }`}
        />
        <div className='invalid-feedback'>
          {errors.confirmPassword?.message?.toString()}
        </div>
      </Form.Group>

      <div className='row'>
        <div className='d-flex justify-content-center align-items-center col-12'>
          <Form.Group className='text-center w-100'>
            <Button
              type='submit'
              className='mt-2 w-100 btn-danger'
              disabled={submitting}
            >
              {submitting ? t('resource_2:changePassword') : t('resource_2:changePassword')}
            </Button>
          </Form.Group>
        </div>
      </div>
    </Form>
  );
};

export default ChangePasswordForm;
